import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Clock,
  Trash2,
  AlertTriangle,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  LayoutGrid,
  List,
  Activity,
  ShieldAlert,
  Globe2,
  AlertCircle,
  ArrowUp,
  CheckCircle,
  ArrowUpDown,
} from 'lucide-react';
import { useFetchSignals } from '../hooks/useFetchSignals';
import { SignalFilters } from '../types';
import api from '../services/api';
import { themeTokens } from '../theme/tokens';
import { DashboardFilters } from '../components/DashboardFilters';

const PAGE_SIZE = 20;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [refreshBanner, setRefreshBanner] = useState<string | null>(null);
  const [pendingSignalsCount, setPendingSignalsCount] = useState<number>(0);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  type IngestionState = 'idle' | 'running' | 'completed' | 'failed' | 'timeout';
  const [ingestionState, setIngestionState] = useState<IngestionState>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const runStartedAtRef = useRef<number | null>(null);

  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return (localStorage.getItem('spacelabs_signal_view_mode') as 'grid' | 'list') || 'grid';
  });

  const handleToggleViewMode = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('spacelabs_signal_view_mode', mode);
  };

  const [showResetModal, setShowResetModal] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);

  const COOLDOWN_KEY = 'spacelabs_ingestion_cooldown_until';

  const [cooldownRemaining, setCooldownRemaining] = useState<number>(() => {
    try {
      const storedUntil = localStorage.getItem(COOLDOWN_KEY);
      if (storedUntil) {
        const remaining = Math.ceil((parseInt(storedUntil, 10) - Date.now()) / 1000);
        return remaining > 0 ? remaining : 0;
      }
    } catch {
      // ignore localStorage failures
    }
    return 0;
  });

  // Check server cooldown status on mount
  useEffect(() => {
    let mounted = true;
    if (typeof api?.ingestion?.getStatus === 'function') {
      api.ingestion
        .getStatus()
        .then((status) => {
          if (!mounted || !status) return;
          if (status.cooldownRemainingSeconds > 0) {
            setCooldownRemaining(status.cooldownRemainingSeconds);
            localStorage.setItem(
              COOLDOWN_KEY,
              (Date.now() + status.cooldownRemainingSeconds * 1000).toString()
            );
          } else {
            setCooldownRemaining(0);
            localStorage.removeItem(COOLDOWN_KEY);
          }

          // Hydrate running ingestion state across page reloads/tabs
          if (status.latestRun && status.latestRun.status === 'running') {
            const runId = status.latestRun.ingestionRunId || 'active';
            setActiveRunId(runId);
            setIngestionState('running');
            const elapsed = status.latestRun.startedAt
              ? Math.max(
                  0,
                  Math.floor((Date.now() - new Date(status.latestRun.startedAt).getTime()) / 1000)
                )
              : Math.max(0, 600 - (status.cooldownRemainingSeconds || 600));
            runStartedAtRef.current = status.latestRun.startedAt
              ? new Date(status.latestRun.startedAt).getTime()
              : Date.now() - elapsed * 1000;
            setElapsedSeconds(elapsed);
            setRefreshBanner(
              'Background ingestion in progress. Monitoring feeds and checking for new signals...'
            );
          }
        })
        .catch(() => {
          // Non-critical, ignore if status endpoint is temporarily unreachable
        });
    }
    return () => {
      mounted = false;
    };
  }, []);

  // Cooldown countdown interval - calculated against wall-clock timestamp to prevent tab throttling drift
  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const syncRemaining = () => {
      const storedUntil = localStorage.getItem(COOLDOWN_KEY);
      if (!storedUntil) {
        setCooldownRemaining(0);
        return;
      }
      const remaining = Math.max(0, Math.ceil((parseInt(storedUntil, 10) - Date.now()) / 1000));
      if (remaining <= 0) {
        localStorage.removeItem(COOLDOWN_KEY);
        setCooldownRemaining(0);
      } else {
        setCooldownRemaining(remaining);
      }
    };

    const timer = setInterval(syncRemaining, 1000);
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  // Re-sync cooldown countdown & elapsed seconds immediately when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const storedUntil = localStorage.getItem(COOLDOWN_KEY);
        if (storedUntil) {
          const remaining = Math.max(0, Math.ceil((parseInt(storedUntil, 10) - Date.now()) / 1000));
          if (remaining <= 0) {
            localStorage.removeItem(COOLDOWN_KEY);
            setCooldownRemaining(0);
          } else {
            setCooldownRemaining(remaining);
          }
        }
        if (runStartedAtRef.current && activeRunId) {
          setElapsedSeconds(Math.max(0, Math.floor((Date.now() - runStartedAtRef.current) / 1000)));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeRunId]);

  // Elapsed timer while background ingestion is active - calculated against wall-clock timestamp
  useEffect(() => {
    if (!activeRunId) {
      runStartedAtRef.current = null;
      return;
    }

    if (!runStartedAtRef.current) {
      runStartedAtRef.current = Date.now() - elapsedSeconds * 1000;
    }

    const timer = setInterval(() => {
      if (runStartedAtRef.current) {
        setElapsedSeconds(Math.max(0, Math.floor((Date.now() - runStartedAtRef.current) / 1000)));
      }
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRunId]);

  // Poll for background ingestion completion when a run has been dispatched
  useEffect(() => {
    if (!activeRunId) return;

    let pollCount = 0;
    const MAX_POLLS = 30; // ~2 minutes at 4-second intervals

    const checkStatus = async () => {
      pollCount += 1;
      try {
        const status = await api.ingestion.getStatus();
        if (status?.latestRun) {
          // Sync elapsed time from startedAt if available and running
          if (status.latestRun.startedAt && status.latestRun.status === 'running') {
            const serverElapsed = Math.max(
              0,
              Math.floor((Date.now() - new Date(status.latestRun.startedAt).getTime()) / 1000)
            );
            setElapsedSeconds((prev) =>
              Math.abs(prev - serverElapsed) > 3 ? serverElapsed : prev
            );
          }

          if (status.latestRun.status === 'completed') {
            const count = status.latestRun.newSignalsCount ?? 0;
            setIngestionState('completed');
            if (count > 0) {
              setPendingSignalsCount(count);
              setRefreshBanner(
                `Ingestion completed: ${count} new signal${count > 1 ? 's' : ''} processed in background.`
              );
            } else {
              setRefreshBanner('Ingestion completed: Feeds checked, 0 new signals.');
            }
            setActiveRunId(null);
            return;
          } else if (status.latestRun.status === 'failed') {
            setIngestionState('failed');
            setRefreshBanner('Ingestion pipeline failed in background.');
            setActiveRunId(null);
            return;
          }
        }
      } catch {
        // Ignore polling errors
      }

      if (pollCount >= MAX_POLLS) {
        setIngestionState('timeout');
        setRefreshBanner(
          'Ingestion pipeline is taking longer than expected. Processing continues in the background.'
        );
        setActiveRunId(null);
      }
    };

    const initialTimer = setTimeout(checkStatus, 300);
    const pollInterval = setInterval(checkStatus, 4000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(pollInterval);
    };
  }, [activeRunId]);

  const handleLoadNewSignals = () => {
    setPendingSignalsCount(0);
    refetch();
  };

  const handleDismissBanner = () => {
    setRefreshBanner(null);
    if (ingestionState !== 'running') {
      setIngestionState('idle');
    }
  };

  const formatCooldown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter state directly from URL search params for persistence
  const page = parseInt(searchParams.get('page') || '1', 10);
  const agencyParam =
    searchParams.get('agency') || searchParams.get('authority') || searchParams.get('source') || '';
  const urgencyParam = searchParams.get('urgency') || '';
  const typeParam = searchParams.get('type') || '';
  const marketParam = searchParams.get('market') || '';
  const productParam = searchParams.get('product') || '';
  const productLineParam =
    searchParams.get('productLine') || searchParams.get('product_line') || '';
  const searchParam = searchParams.get('search') || searchParams.get('q') || '';
  const triageParam = searchParams.get('triage_status') || '';
  const dateRangeParam = searchParams.get('dateRange') || '';
  const sortParam = searchParams.get('sort') || 'newest';

  const activeFilters: SignalFilters = useMemo(() => {
    const filters: SignalFilters = {};
    if (agencyParam) filters.agency = agencyParam;
    if (urgencyParam) filters.urgency = urgencyParam;
    if (typeParam) filters.type = typeParam;
    if (marketParam) filters.market = marketParam;
    if (productParam) filters.product = productParam;
    if (productLineParam) filters.productLine = productLineParam;
    if (searchParam) filters.search = searchParam;
    if (triageParam) filters.triage_status = triageParam;
    if (sortParam) filters.sort = sortParam;
    if (dateRangeParam) {
      const now = new Date();
      if (dateRangeParam === '7d') {
        const d = new Date(now);
        d.setDate(d.getDate() - 7);
        filters.startDate = d.toISOString().split('T')[0];
      } else if (dateRangeParam === '30d') {
        const d = new Date(now);
        d.setDate(d.getDate() - 30);
        filters.startDate = d.toISOString().split('T')[0];
      } else if (dateRangeParam === '90d') {
        const d = new Date(now);
        d.setDate(d.getDate() - 90);
        filters.startDate = d.toISOString().split('T')[0];
      } else if (dateRangeParam === '1y') {
        const d = new Date(now);
        d.setFullYear(d.getFullYear() - 1);
        filters.startDate = d.toISOString().split('T')[0];
      }
      filters.endDate = now.toISOString().split('T')[0];
    }
    return filters;
  }, [
    agencyParam,
    urgencyParam,
    typeParam,
    marketParam,
    productParam,
    productLineParam,
    searchParam,
    triageParam,
    dateRangeParam,
    sortParam,
  ]);

  const { signals, total, stats, loading, error, refetch } = useFetchSignals(
    page,
    activeFilters,
    PAGE_SIZE
  );

  // Quick summary counts for regulatory intelligence strip
  const totalCount = stats?.total ?? total;
  const actionRequiredCount =
    stats?.actionRequired ??
    signals.filter((s) => s.triage_status === 'requires_action' || !s.triage_status).length;
  const criticalCount = stats?.critical ?? signals.filter((s) => s.urgency === 'critical').length;

  const updateUrlFilters = (newParams: Record<string, string | null>) => {
    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);
      Object.entries(newParams).forEach(([key, val]) => {
        if (val === null || val === '') {
          nextParams.delete(key);
        } else {
          nextParams.set(key, val);
        }
      });
      if (!('page' in newParams)) {
        nextParams.set('page', '1');
      }
      return nextParams;
    });
  };

  const filterValues = useMemo(
    () => ({
      agency: agencyParam,
      urgency: urgencyParam,
      type: typeParam,
      market: marketParam,
      triage_status: triageParam,
      productLine: productLineParam,
      product: productParam,
      dateRange: dateRangeParam,
      search: searchParam,
    }),
    [
      agencyParam,
      urgencyParam,
      typeParam,
      marketParam,
      triageParam,
      productLineParam,
      productParam,
      dateRangeParam,
      searchParam,
    ]
  );

  const RETURN_FILTER_STORAGE_KEY = 'spacelabs_dashboard_return_filters';

  useEffect(() => {
    if (!searchParams.toString()) {
      try {
        const saved = sessionStorage.getItem(RETURN_FILTER_STORAGE_KEY);
        if (saved) {
          sessionStorage.removeItem(RETURN_FILTER_STORAGE_KEY);
          setSearchParams(new URLSearchParams(saved), { replace: true });
        }
      } catch {
        // ignore storage errors
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenSignal = (signalId: string) => {
    const currentQuery = searchParams.toString();
    if (currentQuery) {
      try {
        sessionStorage.setItem(RETURN_FILTER_STORAGE_KEY, currentQuery);
      } catch {
        // ignore storage errors
      }
    }
    navigate(`/signals/${signalId}`);
  };

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'search') {
      updateUrlFilters({ search: value || null, q: value || null });
      return;
    }
    if (key === 'agency') {
      updateUrlFilters({ agency: value || null, authority: value || null });
      return;
    }
    if (key === 'productLine') {
      updateUrlFilters({ productLine: value || null, product_line: value || null });
      return;
    }
    updateUrlFilters({ [key]: value || null });
  };

  const clearAllFilters = () => {
    try {
      sessionStorage.removeItem(RETURN_FILTER_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
    setSearchParams(new URLSearchParams());
  };

  const handleRefreshSources = async () => {
    if (cooldownRemaining > 0 || refreshing) return;
    try {
      setRefreshing(true);
      setRefreshBanner(null);
      setElapsedSeconds(0);
      runStartedAtRef.current = Date.now();
      setIngestionState('running');
      const res = await api.ingestion.trigger();
      const seconds = res.cooldownRemainingSeconds ?? 600;
      setCooldownRemaining(seconds);
      localStorage.setItem(COOLDOWN_KEY, (Date.now() + seconds * 1000).toString());

      const execName = res.executionId?.includes(':')
        ? res.executionId.split(':').pop()
        : res.executionId || 'INGEST-RUN';

      setRefreshBanner(
        `Ingestion started: Dispatched ${res.sources?.length || 7} sources to background pipeline (${execName}). Checking for new signals...`
      );
      setActiveRunId(res.ingestionRunId || res.executionId || 'active');
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          status?: number;
          data?: { error?: string; cooldownRemainingSeconds?: number };
        };
      };
      if (axiosErr.response?.status === 429 && axiosErr.response.data?.cooldownRemainingSeconds) {
        const secs = axiosErr.response.data.cooldownRemainingSeconds;
        setCooldownRemaining(secs);
        localStorage.setItem(COOLDOWN_KEY, (Date.now() + secs * 1000).toString());
        setRefreshBanner(
          `Ingestion pipeline is on cooldown. Next refresh available in ${formatCooldown(secs)}.`
        );
        setIngestionState('idle');
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to trigger ingestion pipeline.';
        setRefreshBanner(`Ingestion trigger failed: ${message}`);
        setIngestionState('failed');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleConfirmReset = async () => {
    setResetting(true);
    try {
      const res = await api.signals.resetDatabase();
      setRefreshBanner(
        `Database reset complete: ${res.deleted.classifiedDeleted} classified signals and ${res.deleted.rawDeleted} raw signals deleted.`
      );
      setShowResetModal(false);
      await refetch();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset signals database.';
      setRefreshBanner(`Reset failed: ${message}`);
    } finally {
      setResetting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const blob = await api.signals.exportSignals(activeFilters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      a.download = `Spacelabs_Signals_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setRefreshBanner('Signals exported successfully to Excel (CSV).');
    } catch (err: unknown) {
      console.error('Export failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to export signals.';
      setRefreshBanner(`Export failed: ${message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 pb-20 font-sans">
      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-6">
        {/* Banner Messages */}
        {refreshBanner && (
          <div
            className={`rounded-lg p-4 border text-xs flex items-center justify-between shadow-xs transition-colors ${
              ingestionState === 'completed'
                ? pendingSignalsCount > 0
                  ? 'bg-emerald-50/90 border-emerald-200 text-emerald-950'
                  : 'bg-slate-50 border-slate-200 text-slate-800'
                : ingestionState === 'timeout'
                  ? 'bg-amber-50/90 border-amber-200 text-amber-950'
                  : ingestionState === 'failed'
                    ? 'bg-rose-50/90 border-rose-200 text-rose-950'
                    : 'bg-blue-50/90 border-blue-200 text-blue-950'
            }`}
          >
            <div className="flex items-center gap-2.5">
              {ingestionState === 'running' ? (
                <RefreshCw className="w-4 h-4 text-blue-600 animate-spin shrink-0" />
              ) : ingestionState === 'completed' ? (
                <CheckCircle
                  className={`w-4 h-4 shrink-0 ${
                    pendingSignalsCount > 0 ? 'text-emerald-600' : 'text-slate-500'
                  }`}
                />
              ) : ingestionState === 'timeout' ? (
                <Clock className="w-4 h-4 text-amber-600 shrink-0" />
              ) : ingestionState === 'failed' ? (
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse shrink-0"></span>
              )}
              <p className="font-medium">
                {refreshBanner}
                {ingestionState === 'running' && elapsedSeconds > 0 && (
                  <span className="ml-2 inline-flex items-center text-[10px] font-semibold text-blue-700 bg-blue-100/70 px-2 py-0.5 rounded-full">
                    {elapsedSeconds}s elapsed
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={handleDismissBanner}
              className="text-slate-500 hover:text-slate-800 p-1 cursor-pointer transition-colors"
              title="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Executive Hero & KPI Strip */}
        <div className="bg-white rounded-xl border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] overflow-hidden">
          {/* Top Hero Section */}
          <div className="p-6 md:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-800 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded">
                  {activeRunId ? (
                    <>
                      <RefreshCw className="w-3 h-3 text-blue-600 animate-spin" />
                      Ingesting Feeds ({elapsedSeconds}s)
                    </>
                  ) : (
                    <>
                      <Activity className="w-3 h-3 text-blue-600" />
                      Live Surveillance
                    </>
                  )}
                </span>
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-[-0.02em] leading-tight">
                Regulatory Digest
              </h1>
            </div>

            {/* Action Suite */}
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                onClick={handleExportExcel}
                disabled={exporting || loading || signals.length === 0}
                className="bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-800 border border-slate-300 disabled:opacity-50 text-xs font-semibold px-4 py-2.5 rounded-lg transition flex items-center gap-2 shadow-xs cursor-pointer"
                title="Export filtered signals to Excel (CSV)"
              >
                <Download
                  className={`w-3.5 h-3.5 text-blue-700 ${exporting ? 'animate-spin' : ''}`}
                />
                <span>{exporting ? 'Exporting...' : 'Export to Excel'}</span>
              </button>
              <button
                onClick={handleRefreshSources}
                disabled={refreshing || cooldownRemaining > 0}
                className={`text-xs font-semibold px-4 py-2.5 rounded-lg transition flex items-center gap-2 shadow-xs select-none ${
                  cooldownRemaining > 0
                    ? 'bg-slate-100 border border-slate-200 text-slate-500 cursor-not-allowed opacity-90'
                    : 'bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white disabled:opacity-50 cursor-pointer'
                }`}
                title={
                  cooldownRemaining > 0
                    ? `Ingestion pipeline cooldown active. Next refresh available in ${formatCooldown(cooldownRemaining)}.`
                    : 'Trigger global regulatory feeds ingestion pipeline'
                }
              >
                {refreshing ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 text-white animate-spin" />
                    <span>Refreshing Feeds...</span>
                  </>
                ) : cooldownRemaining > 0 ? (
                  <>
                    <Clock className="w-3.5 h-3.5 text-slate-500 animate-pulse" />
                    <span>Refresh in {formatCooldown(cooldownRemaining)}</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 text-white" />
                    <span>Refresh Sources</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* KPI Metrics Strip - Actionable Regulatory Intelligence */}
          <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-slate-100 bg-slate-50/60 divide-y sm:divide-y-0 sm:divide-x divide-slate-100 text-xs">
            {/* Total Monitored Signals */}
            <div className="p-4">
              <div className="text-slate-500 font-medium flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5 text-blue-600" />
                Total Intelligence
              </div>
              <div className="text-xl font-bold text-slate-900 font-sans mt-0.5 tabular-nums">
                {totalCount}
              </div>
            </div>

            {/* Action Required / Triage Backlog */}
            <div className="p-4">
              <div className="text-slate-500 font-medium flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                Action Required
              </div>
              <div className="text-xl font-bold text-amber-700 font-sans mt-0.5 tabular-nums">
                {actionRequiredCount}
              </div>
            </div>

            {/* Critical Priority Alerts */}
            <div className="p-4">
              <div className="text-slate-500 font-medium flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
                Critical Priority
              </div>
              <div className="text-xl font-bold text-rose-700 font-sans mt-0.5 tabular-nums">
                {criticalCount}
              </div>
            </div>
          </div>
        </div>

        {/* Precision Command Toolbar */}
        <DashboardFilters
          values={filterValues}
          onChange={handleFilterChange}
          onClearAll={clearAllFilters}
        />

        {/* Error State */}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 border border-red-200 text-xs text-red-900 font-medium">
            {error}
          </div>
        )}

        {/* Social-feed style 'X new signals available' pill */}
        {pendingSignalsCount > 0 && (
          <div className="flex justify-center my-3 sticky top-4 z-20">
            <button
              onClick={handleLoadNewSignals}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5"
            >
              <ArrowUp className="w-3.5 h-3.5 animate-bounce" />
              <span>
                {pendingSignalsCount} new signal{pendingSignalsCount > 1 ? 's' : ''} available ·
                Refresh feed
              </span>
            </button>
          </div>
        )}

        {/* Signals List / Empty State / Loading State */}
        {loading ? (
          <div className="bg-white rounded-xl p-16 text-center border border-slate-200 shadow-xs">
            <div className="inline-block w-8 h-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-slate-600 text-xs font-semibold">
              Surveilling regulatory sources and processing signals...
            </p>
          </div>
        ) : signals.length === 0 ? (
          <div className="bg-white rounded-xl p-16 text-center border border-slate-200 shadow-xs space-y-3">
            <Search className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="font-display text-xl font-semibold text-slate-900">
              No matching regulatory signals
            </h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
              No signals match your specific filter combinations. Try adjusting search queries or
              broadening authority and priority filters.
            </p>
            {Object.keys(activeFilters).length > 0 && (
              <button
                onClick={clearAllFilters}
                className="mt-2 bg-blue-50 text-blue-900 border border-blue-200 text-xs font-semibold px-4 py-2 rounded-lg hover:bg-blue-100 transition cursor-pointer"
              >
                Reset All Filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* View Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-1">
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-600 font-medium">
                  Showing <strong className="text-slate-900 tabular-nums">{signals.length}</strong>{' '}
                  of <strong className="text-slate-900 tabular-nums">{total}</strong> regulatory
                  signals
                </div>
                <div className="hidden sm:block h-3.5 w-px bg-slate-200" />
                <div className="flex items-center gap-1.5">
                  <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
                  <label
                    htmlFor="dashboard-sort-select"
                    className="text-xs text-slate-500 font-medium"
                  >
                    Sort:
                  </label>
                  <select
                    id="dashboard-sort-select"
                    data-testid="dashboard-sort-select"
                    value={sortParam}
                    onChange={(e) => updateUrlFilters({ sort: e.target.value })}
                    className="text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer shadow-xs hover:border-slate-300 transition"
                    aria-label="Sort regulatory signals"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="urgency_desc">Highest Urgency</option>
                    <option value="urgency_asc">Lowest Urgency</option>
                    <option value="title_asc">Title (A–Z)</option>
                    <option value="title_desc">Title (Z–A)</option>
                  </select>
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-xs">
                <button
                  onClick={() => handleToggleViewMode('grid')}
                  className={`px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
                    viewMode === 'grid'
                      ? 'bg-blue-50 text-blue-900 shadow-xs'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                  title="Card Grid View"
                  aria-label="Card Grid View"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Cards</span>
                </button>
                <button
                  onClick={() => handleToggleViewMode('list')}
                  className={`px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
                    viewMode === 'list'
                      ? 'bg-blue-50 text-blue-900 shadow-xs'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                  title="Compact Table List View"
                  aria-label="Compact Table List View"
                >
                  <List className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>
            </div>

            {viewMode === 'grid' ? (
              /* Elevated Signal Cards Grid */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {signals.map((signal) => {
                  const urgencyStyle =
                    (signal.urgency && themeTokens.colors.urgency[signal.urgency]) ||
                    themeTokens.colors.urgency.medium;
                  const agencyKey = (signal.agency ||
                    'DEFAULT') as keyof typeof themeTokens.colors.agency;
                  const agencyStyle =
                    themeTokens.colors.agency[agencyKey] || themeTokens.colors.agency.DEFAULT;
                  return (
                    <div
                      key={signal.id}
                      onClick={() => handleOpenSignal(signal.id)}
                      className="bg-white rounded-lg p-5 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] hover:shadow-md hover:border-slate-300 transition-all duration-150 cursor-pointer flex flex-col justify-between group space-y-3.5"
                    >
                      <div className="space-y-2.5">
                        {/* Top Meta Strip */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`px-2.5 py-0.5 rounded text-[11px] font-semibold border ${agencyStyle}`}
                            >
                              {signal.agency || 'REGULATORY'}
                            </span>
                            <span
                              className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${urgencyStyle}`}
                            >
                              {(signal.urgency || 'Unrated').toUpperCase()} PRIORITY
                            </span>
                            <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[11px] font-medium border border-slate-200 capitalize">
                              {signal.type}
                            </span>
                          </div>

                          <span className="text-xs text-slate-500 font-medium tabular-nums shrink-0">
                            {new Date(
                              signal.published_date || signal.created_at
                            ).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </div>

                        {/* Title with Newsreader Serif */}
                        <h3 className="font-display text-lg font-semibold text-slate-900 group-hover:text-blue-700 transition-colors leading-snug tracking-[-0.01em]">
                          {signal.title || signal.summary}
                        </h3>

                        {/* Summary preview */}
                        {signal.title && signal.title !== signal.summary && (
                          <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed font-sans">
                            {signal.summary}
                          </p>
                        )}
                      </div>

                      {/* Card Footer Bar */}
                      <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <div className="truncate pr-2">
                          <span className="font-semibold text-slate-600">Products: </span>
                          <span className="text-slate-800 font-medium">
                            {signal.products && signal.products.length > 0
                              ? signal.products.join(', ')
                              : 'Portfolio General'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* High-Density List / Table View */
              <div className="bg-white rounded-lg border border-slate-200 shadow-xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs font-sans">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                        <th className="py-3 px-4">Authority</th>
                        <th className="py-3 px-4">Title / Summary</th>
                        <th className="py-3 px-4">Urgency</th>
                        <th className="py-3 px-4">Markets & Products</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {signals.map((signal) => {
                        const urgencyStyle =
                          (signal.urgency && themeTokens.colors.urgency[signal.urgency]) ||
                          themeTokens.colors.urgency.medium;
                        const agencyKey = (signal.agency ||
                          'DEFAULT') as keyof typeof themeTokens.colors.agency;
                        const agencyStyle =
                          themeTokens.colors.agency[agencyKey] || themeTokens.colors.agency.DEFAULT;

                        return (
                          <tr
                            key={signal.id}
                            onClick={() => handleOpenSignal(signal.id)}
                            className="hover:bg-slate-50 transition-colors cursor-pointer group"
                          >
                            <td className="py-3 px-4 whitespace-nowrap font-medium text-slate-700">
                              <span className={`px-2 py-0.5 rounded text-xs border ${agencyStyle}`}>
                                {signal.agency || 'FDA CDRH'}
                              </span>
                            </td>
                            <td className="py-3 px-4 max-w-xs md:max-w-md">
                              <div className="font-display text-sm font-semibold text-slate-900 group-hover:text-blue-700 transition-colors line-clamp-1">
                                {signal.title || signal.summary}
                              </div>
                              <div className="text-[11px] text-slate-500 line-clamp-1 mt-0.5 font-sans">
                                {signal.summary}
                              </div>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${urgencyStyle}`}
                              >
                                {(signal.urgency || 'Unrated').toUpperCase()} PRIORITY
                              </span>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-slate-600 text-[11px]">
                              <div>{signal.markets?.join(', ') || 'Global'}</div>
                              <div className="text-slate-500 truncate max-w-[140px]">
                                {signal.products?.join(', ') || 'General'}
                              </div>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span className="font-medium capitalize text-xs text-slate-700">
                                {signal.triage_status?.replace('_', ' ') || 'requires action'}
                              </span>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-slate-500 text-xs font-mono tabular-nums">
                              {new Date(
                                signal.published_date || signal.created_at
                              ).toLocaleDateString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            <div className="flex justify-between items-center pt-4">
              <button
                onClick={() => updateUrlFilters({ page: String(Math.max(1, page - 1)) })}
                disabled={page <= 1}
                className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition cursor-pointer flex items-center gap-1.5"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Previous Page
              </button>
              <span className="text-xs text-slate-600 font-medium tabular-nums">
                Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <button
                onClick={() => updateUrlFilters({ page: String(page + 1) })}
                disabled={page >= Math.ceil(total / PAGE_SIZE)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition cursor-pointer flex items-center gap-1.5"
              >
                Next Page <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Floating Reset Database Action Button */}
      <div className="fixed bottom-20 right-6 lg:bottom-6 z-40 group no-print">
        <button
          onClick={() => setShowResetModal(true)}
          title="Reset Signals Database"
          aria-label="Reset Signals Database"
          className="w-11 h-11 bg-rose-700 hover:bg-rose-800 active:scale-95 text-white rounded-full shadow-md border border-rose-600 transition-all duration-150 flex items-center justify-center cursor-pointer"
        >
          <Trash2 className="w-4 h-4 text-white" />
        </button>
        <span className="absolute right-0 bottom-full mb-2 hidden group-hover:block bg-slate-950 text-white text-xs font-medium px-2.5 py-1 rounded shadow-lg whitespace-nowrap">
          Reset Signals DB
        </span>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 border border-slate-200 shadow-xl space-y-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="w-9 h-9 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center text-xl shrink-0">
                <AlertTriangle className="w-4 h-4 text-rose-700" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-slate-900 leading-tight">
                  Reset Signals Database?
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Permanent Data Deletion Warning</p>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed font-sans">
              This action will{' '}
              <strong className="text-rose-800 font-semibold">
                permanently delete all raw and classified regulatory signals
              </strong>{' '}
              from the database. All associated triage history and internal notes will be removed.
            </p>
            <div className="bg-rose-50 rounded-md p-3 border border-rose-200 text-xs text-rose-900 font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0" />
              <span>This action cannot be undone. Are you sure you want to proceed?</span>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowResetModal(false)}
                disabled={resetting}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 active:scale-[0.98] rounded-md transition cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReset}
                disabled={resetting}
                className="px-4 py-2 text-xs font-semibold text-white bg-rose-700 hover:bg-rose-800 active:scale-[0.98] disabled:opacity-50 rounded-md transition flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                {resetting ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Resetting Database...</span>
                  </>
                ) : (
                  <span>Confirm Reset</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
